import { useEffect, useRef, useMemo } from 'react';
import { useDatabase } from '@/context/DatabaseContext';
import { organizeProjectsFromDatabase, recomputeTaskCounts, fetchCategoriesFromDatabase, organizeCategories, recomputeCategoryTaskCounts } from '@/utils/organizeProjects';
import { getCachedFolders, getCachedLists, getStructureCacheMetadata } from '@/utils/structureCache';
import { getAllFolders, getAllLists } from '@/db/projects';
import { isIndexedDBAvailable } from '@/utils/taskCache';
import { useAppStore } from '@/store/appStore';
import { supabase } from '@/lib/supabaseClient';
import { logger } from '@/utils/logger';
import { CACHE_MAX_AGE_MS, REALTIME_INSERT_DEBOUNCE_MS } from '@/constants/timings';
import { useShallowMemoizedMap } from './useShallowMemoizedMap';
import { useOrganization } from '@/context/OrganizationContext';

/**
* Hook principale per gestire gli spaces (progetti) dell'applicazione.
* 
* Responsabilità:
* 1. Carica gli spaces dal database con conteggi dei task
* 2. Carica folders e lists mappings con caching ottimizzato
* 3. Aggiorna i conteggi dei task quando i task cambiano
* 4. Gestisce lo stato di loading e previene race conditions
* 
* Ottimizzazioni:
* - Usa cache IndexedDB per caricamento istantaneo
* - Carica spaces immediatamente senza attendere i task
* - Aggiorna i conteggi dei task solo quando cambiano significativamente (>100 task)
* - Usa hash degli spaces per rilevare cambiamenti strutturali
* - Previene race conditions con flag di sincronizzazione
* 
* @returns Oggetto con spaces, foldersMap, listsMap e stato di loading
*/
export function useSpaces() {
  const { tasks } = useDatabase();
  const { currentOrg } = useOrganization();
  const currentOrgId = currentOrg?.id;
  const setSpacesLoading = useAppStore((state) => state.setSpacesLoading);
  const spacesLoading = useAppStore((state) => state.spacesLoading);
  const spacesRaw = useAppStore((state) => state.spaces);
  // Ensure spaces is always an array (defensive check for store rehydration)
  const spaces = useMemo(() => Array.isArray(spacesRaw) ? spacesRaw : [], [spacesRaw]);
  const categoriesRaw = useAppStore((state) => state.categories);
  const categories = useMemo(() => Array.isArray(categoriesRaw) ? categoriesRaw : [], [categoriesRaw]);
  const setCategories = useAppStore((state) => state.setCategories);
  const foldersMapObj = useAppStore((state) => state.foldersMap);
  const listsMapObj = useAppStore((state) => state.listsMap);
  const setSpaces = useAppStore((state) => state.setSpaces);
  const setFoldersMap = useAppStore((state) => state.setFoldersMap);
  const setListsMap = useAppStore((state) => state.setListsMap);
  const setSpacesInitialLoadDone = useAppStore((state) => state.setSpacesInitialLoadDone);
  const spacesInitialLoadDone = useAppStore((state) => state.spacesInitialLoadDone);
  const spacesLoadingInProgress = useAppStore((state) => state.spacesLoadingInProgress);
  const setSpacesLoadingInProgress = useAppStore((state) => state.setSpacesLoadingInProgress);

  // Convert objects to Maps
  // Use efficient shallow comparison instead of JSON.stringify - only recreate if actual content changes
  const foldersMap = useShallowMemoizedMap<{ name: string; spaceId: string }>(foldersMapObj);
  const listsMap = useShallowMemoizedMap<{ name: string; folderId: string | null; spaceId: string }>(listsMapObj);

  // Load folders and lists mappings
  // OPTIMIZATION: Load once when spaces are first loaded, use cache if available
  // Uses hash of space IDs to detect structural changes and avoid unnecessary reloads
  const foldersListsLoadedRef = useRef(false);
  const lastSpacesHashForFoldersRef = useRef<string>('');

  useEffect(() => {
  if (spaces.length === 0) {
      return;
  }

  // Check if spaces structure actually changed
  const currentSpacesHash = spaces.map(s => s.id).sort().join(',');
  if (foldersListsLoadedRef.current && currentSpacesHash === lastSpacesHashForFoldersRef.current) {
      return; // Spaces structure hasn't changed, skip
  }
  foldersListsLoadedRef.current = true;
  lastSpacesHashForFoldersRef.current = currentSpacesHash;

  // Try to load from cache first
  const loadFoldersLists = async () => {
      const foldersListsStartTime = performance.now();

      // Try cache first
      if (isIndexedDBAvailable()) {
    try {
          const [cachedFolders, cachedLists, metadata] = await Promise.all([
      getCachedFolders(),
      getCachedLists(),
      getStructureCacheMetadata(),
          ]);

          const cacheAge = metadata ? Date.now() - metadata.lastSyncTime : Infinity;

          if (cachedFolders.length >= 0 && cachedLists.length >= 0 && cacheAge < CACHE_MAX_AGE_MS) {
      // Use cache
      const foldersListsTime = performance.now() - foldersListsStartTime;
      logger.debug(`[Cache] Folders/Lists loaded from cache in ${foldersListsTime.toFixed(2)}ms (${cachedFolders.length} folders, ${cachedLists.length} lists)`);

      const foldersMapObj: Record<string, { name: string; spaceId: string }> = {};
      cachedFolders.forEach((f) => {
              foldersMapObj[f.id] = { name: f.name, spaceId: f.project_id };
      });
      setFoldersMap(foldersMapObj);

      const listsMapObj: Record<string, { name: string; folderId: string | null; spaceId: string }> = {};
      cachedLists.forEach((l) => {
              listsMapObj[l.id] = { name: l.name, folderId: l.folder_id, spaceId: l.project_id };
      });
      setListsMap(listsMapObj);

      // Sync in background if cache is getting old
      if (cacheAge > CACHE_MAX_AGE_MS * 0.7) {
              Promise.all([getAllFolders(spaces.map(s => ({ id: s.id, name: s.name }))), getAllLists(spaces.map(s => ({ id: s.id, name: s.name })))]).then(([folders, lists]) => {
        const foldersMapObj: Record<string, { name: string; spaceId: string }> = {};
        folders.forEach(f => {
                  foldersMapObj[f.id] = { name: f.name, spaceId: f.project_id };
        });
        setFoldersMap(foldersMapObj);

        const listsMapObj: Record<string, { name: string; folderId: string | null; spaceId: string }> = {};
        lists.forEach(l => {
                  listsMapObj[l.id] = { name: l.name, folderId: l.folder_id, spaceId: l.project_id };
        });
        setListsMap(listsMapObj);
              }).catch((error) => logger.error('Background sync error:', error));
      }
      return;
          }
    } catch (error) {
          logger.error('Failed to load folders/lists from cache:', error);
    }
      }

      // Fallback to Supabase if cache unavailable or expired
      // OPTIMIZATION: Pass spaces to avoid redundant query
      Promise.all([getAllFolders(spaces.map(s => ({ id: s.id, name: s.name }))), getAllLists(spaces.map(s => ({ id: s.id, name: s.name })))]).then(([folders, lists]) => {
    const foldersListsTime = performance.now() - foldersListsStartTime;
    logger.log(`[Supabase] Folders/Lists loaded from Supabase in ${foldersListsTime.toFixed(2)}ms (${folders.length} folders, ${lists.length} lists)`);
    const foldersMapObj: Record<string, { name: string; spaceId: string }> = {};
    folders.forEach(f => {
          foldersMapObj[f.id] = { name: f.name, spaceId: f.project_id };
    });
    setFoldersMap(foldersMapObj);

    const listsMapObj: Record<string, { name: string; folderId: string | null; spaceId: string }> = {};
    lists.forEach(l => {
          listsMapObj[l.id] = { name: l.name, folderId: l.folder_id, spaceId: l.project_id };
    });
    setListsMap(listsMapObj);
      }).catch((error) => {
    logger.error('[Cache] Failed to load folders/lists:', error);
    foldersListsLoadedRef.current = false; // Allow retry on error
      });
  };

  loadFoldersLists();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally omitting setFoldersMap and setListsMap to avoid infinite loops
  }, [spaces]); // Re-run when spaces are loaded

  // Stabilize tasks reference to avoid unnecessary re-renders
  const tasksHash = useMemo(() => {
  if (tasks.length === 0) return '';
  // Create a hash from first 10 task IDs and total count
  const ids = tasks.slice(0, 10).map(t => t.id).join(',');
  return `${tasks.length}:${ids}`;
  }, [tasks]);

  // Track if initial load has happened - use store to share across instances
  const lastTaskCountRef = useRef<number>(0);
  const lastSpacesHashRef = useRef<string>('');

  // Load spaces structure from database with task counts
  // IMPORTANT: Load spaces immediately, then update task counts when tasks are available
  // This allows projects to show up quickly even if tasks are still loading
  useEffect(() => {
  // Skip if already loaded or loading in this session
  if (spacesInitialLoadDone || spacesLoadingInProgress) {
      return;
  }
  // Set flags IMMEDIATELY before starting async operation to prevent race conditions
  setSpacesInitialLoadDone(true);
  setSpacesLoadingInProgress(true);

  // If spaces were rehydrated from Zustand persist, keep them visible immediately
  // (with their previous session's task counts) and refresh structure in background.
  const hasCachedSpaces = spaces.length > 0;
  if (hasCachedSpaces) {
      // Cached spaces already rendered — no spinner needed.
      // Initialize refs from cached data so the task-count effect doesn't skip them.
      lastSpacesHashRef.current = spaces.map(s => s.id).sort().join(',');
      setSpacesLoadingInProgress(false);

      // Background refresh: update structure only, preserve cached task counts
      // until real tasks arrive and recomputeTaskCounts runs via tasksHash effect.
      Promise.all([
        organizeProjectsFromDatabase(undefined, { orgId: currentOrgId }),
        fetchCategoriesFromDatabase(currentOrgId),
      ]).then(([loadedSpaces, dbCategories]) => {
        const newHash = loadedSpaces.map(s => s.id).sort().join(',');
        // Always update spaces: structure (folders/rootLists order) may change without project IDs changing
        {
          // Structure changed (new/removed projects) — merge with cached counts
          const currentTasks = tasksRef.current;
          let finalSpaces = loadedSpaces;
          if (currentTasks.length > 0) {
            finalSpaces = recomputeTaskCounts(loadedSpaces, currentTasks);
          } else {
            // No tasks yet: carry over cached counts for matching spaces
            const cachedById = new Map(spaces.map(s => [s.id, s]));
            finalSpaces = loadedSpaces.map(s => {
              const cached = cachedById.get(s.id);
              return cached ? { ...s, taskCount: cached.taskCount } : s;
            });
          }
          setSpaces(finalSpaces);
          lastSpacesHashRef.current = newHash;
        }
        if (dbCategories.length > 0) {
          const currentSpaces = useAppStore.getState().spaces;
          setCategories(organizeCategories(dbCategories, currentSpaces));
        }
      }).catch((error) => {
        logger.error('[Cache] Background refresh error:', error);
      });
      return;
  }

  // No cached spaces — show spinner and load from scratch
  setSpacesLoading(true);

  const spacesLoadStartTime = performance.now();
  Promise.all([
    organizeProjectsFromDatabase(
      tasksRef.current.length > 0 ? tasksRef.current : undefined,
      { orgId: currentOrgId },
    ),
    fetchCategoriesFromDatabase(currentOrgId),
  ])
      .then(([loadedSpaces, dbCategories]) => {
    const spacesLoadTime = performance.now() - spacesLoadStartTime;
    logger.debug(`[Cache] Spaces loaded in ${spacesLoadTime.toFixed(2)}ms (${loadedSpaces.length} spaces, ${dbCategories.length} categories)`);
    const currentTasks = tasksRef.current;
    let finalSpaces = loadedSpaces;
    if (currentTasks.length > 0) {
      finalSpaces = recomputeTaskCounts(loadedSpaces, currentTasks);
    }
    setSpaces(finalSpaces);
    if (dbCategories.length > 0) {
      const organized = organizeCategories(dbCategories, finalSpaces);
      setCategories(organized);
    }
    setSpacesLoading(false);
    setSpacesLoadingInProgress(false);
    lastSpacesHashRef.current = finalSpaces.map(s => s.id).sort().join(',');
    lastTaskCountRef.current = currentTasks.length;
      })
      .catch((error) => {
    logger.error('[Cache] Error loading spaces:', error);
    setSpacesLoading(false);
    setSpacesLoadingInProgress(false);
    setSpaces([]);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally empty deps to load once on mount
  }, []); // Load once on mount

  // Re-load spaces when organization changes
  const prevOrgIdRef = useRef(currentOrgId);
  useEffect(() => {
    if (prevOrgIdRef.current === currentOrgId) return;
    prevOrgIdRef.current = currentOrgId;
    
    setSpacesLoading(true);
    Promise.all([
      organizeProjectsFromDatabase(
        tasksRef.current.length > 0 ? tasksRef.current : undefined,
        { forceRefresh: true, orgId: currentOrgId },
      ),
      fetchCategoriesFromDatabase(currentOrgId),
    ])
      .then(([loadedSpaces, dbCategories]) => {
        const currentTasks = tasksRef.current;
        let finalSpaces = loadedSpaces;
        if (currentTasks.length > 0) {
          finalSpaces = recomputeTaskCounts(loadedSpaces, currentTasks);
        }
        setSpaces(finalSpaces);
        if (dbCategories.length > 0) {
          const organized = organizeCategories(dbCategories, finalSpaces);
          setCategories(organized);
        }
        setSpacesLoading(false);
        lastSpacesHashRef.current = finalSpaces.map(s => s.id).sort().join(',');
        lastTaskCountRef.current = currentTasks.length;
      })
      .catch((error) => {
        logger.error('[Cache] Error reloading spaces for org:', error);
        setSpacesLoading(false);
      });
  }, [currentOrgId, setSpaces, setSpacesLoading, setCategories]);

  // Store tasks in a ref to avoid dependency issues
  // IMPORTANT: Update synchronously during render (not via useEffect) so that
  // async .then() callbacks see the latest tasks immediately
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  // Lightweight task count recomputation when tasks change
  // Uses pure function instead of re-querying Supabase
  useEffect(() => {
    if (!spacesInitialLoadDone || spaces.length === 0) return;
    const currentTasks = tasksRef.current;
    if (currentTasks.length === 0) return;
    // Skip if we already recomputed with this exact task count,
    // UNLESS lastTaskCountRef is 0 (first time tasks arrive after cached spaces)
    if (lastTaskCountRef.current > 0 && currentTasks.length === lastTaskCountRef.current) return;
    lastTaskCountRef.current = currentTasks.length;

    const updated = recomputeTaskCounts(spaces, currentTasks);
    if (updated.some((s, i) => s !== spaces[i])) {
      setSpaces(updated);
      // Recompute category task counts from updated spaces
      if (categories.length > 0) {
        const updatedCategories = categories.map(cat => ({
          ...cat,
          projects: cat.projects.map(p => updated.find(s => s.id === p.id) || p),
        }));
        const recomputed = recomputeCategoryTaskCounts(updatedCategories);
        if (recomputed.some((c, i) => c !== categories[i])) {
          setCategories(recomputed);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksHash]);

  // Real-time subscription for spaces table changes (covers spaces, folders, and lists)
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    const handleStructureChange = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(async () => {
        try {
          const [loadedSpaces, dbCategories] = await Promise.all([
            organizeProjectsFromDatabase(
              tasksRef.current.length > 0 ? tasksRef.current : undefined,
              { forceRefresh: true, orgId: currentOrgId },
            ),
            fetchCategoriesFromDatabase(currentOrgId),
          ]);
          setSpaces(loadedSpaces);
          if (dbCategories.length > 0) {
            setCategories(organizeCategories(dbCategories, loadedSpaces));
          }
          lastSpacesHashRef.current = loadedSpaces.map(s => s.id).sort().join(',');

          // Refresh folders and lists maps
          const spacesList = loadedSpaces.map(s => ({ id: s.id, name: s.name }));
          const [folders, lists] = await Promise.all([
            getAllFolders(spacesList),
            getAllLists(spacesList),
          ]);

          const newFoldersMap: Record<string, { name: string; spaceId: string }> = {};
          folders.forEach(f => { newFoldersMap[f.id] = { name: f.name, spaceId: f.project_id }; });
          setFoldersMap(newFoldersMap);

          const newListsMap: Record<string, { name: string; folderId: string | null; spaceId: string }> = {};
          lists.forEach(l => { newListsMap[l.id] = { name: l.name, folderId: l.folder_id, spaceId: l.project_id }; });
          setListsMap(newListsMap);
        } catch (error) {
          logger.error('Real-time structure refresh failed:', error);
        }
      }, REALTIME_INSERT_DEBOUNCE_MS);
    };

    const channel = supabase
      .channel('spaces-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'spaces' }, handleStructureChange)
      .subscribe();

    return () => {
      clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [currentOrgId, setSpaces, setCategories, setFoldersMap, setListsMap]);

  // Build containersMap from spaces/folders/lists for unified access
  // Paths are ID-based for stable URL routing; displayPath is name-based for UI display
  const containersMap = useMemo(() => {
  const map = new Map<string, { _id: string; type: 'space' | 'folder' | 'list'; name: string; path: string; displayPath: string }>();

  // Ensure spaces is an array
  const spacesArray = Array.isArray(spaces) ? spaces : [];

  // Ensure foldersMap and listsMap are Maps
  const foldersMapSafe = foldersMap instanceof Map ? foldersMap : new Map();
  const listsMapSafe = listsMap instanceof Map ? listsMap : new Map();

  // Add spaces
  spacesArray.forEach(space => {
      map.set(space.id, {
    _id: space.id,
    type: 'space',
    name: space.name,
    path: space.id,
    displayPath: space.name,
      });
  });

  // Add folders (construct path from space ID / folder ID)
  foldersMapSafe.forEach((folder, folderId) => {
      const space = spacesArray.find(s => s.id === folder.spaceId);
      if (space) {
    map.set(folderId, {
          _id: folderId,
          type: 'folder',
          name: folder.name,
          path: `${space.id}/${folderId}`,
          displayPath: `${space.name}/${folder.name}`,
    });
      }
  });

  // Add lists (construct path from IDs)
  listsMapSafe.forEach((list, listId) => {
      const space = spacesArray.find(s => s.id === list.spaceId);
      if (space) {
    if (list.folderId) {
          const folder = foldersMapSafe.get(list.folderId);
          if (folder) {
      map.set(listId, {
              _id: listId,
              type: 'list',
              name: list.name,
              path: `${space.id}/${list.folderId}/${listId}`,
              displayPath: `${space.name}/${folder.name}/${list.name}`,
      });
          }
    } else {
          map.set(listId, {
      _id: listId,
      type: 'list',
      name: list.name,
      path: `${space.id}/${listId}`,
      displayPath: `${space.name}/${list.name}`,
          });
    }
      }
  });

  return map;
  }, [spaces, foldersMap, listsMap]);

  return {
  spaces: Array.isArray(spaces) ? spaces : [],
  categories,
  foldersMap,
  listsMap,
  containersMap: containersMap || new Map(),
  spacesLoading,
  };
}


